import { cn, focusRing, Skeleton } from "@simpro/ui";
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { Link, useLocation } from "react-router";
import { salonProfileQuery } from "@/features/salon";
import { formatPhone } from "@/lib/formatPhone";
import { useMediaQuery } from "@/shared/hooks/useMediaQuery";
import { useScrolledPast } from "@/shared/hooks/useScrolledPast";
import { pill } from "@/shared/styles/pill";
import { AccountButton } from "./AccountButton";
import { SalonMark } from "./SalonMark";
import { Switcher } from "./Switcher";
import { ThemeToggle } from "./ThemeToggle";

/** The phone header at rest, in pixels: 14 + a 48 row (the logo) + 12 + a 52 switcher + 8. */
const PHONE_REST_HEIGHT = 134;

/** Where the booking flow puts its step pills, inside the header row on a desktop (design note B2 to B5). */
export const BOOKING_PROGRESS_SLOT = "booking-progress-slot";

/**
 * The salon's name and logo, and the way to its menu, the customer's bookings and their account.
 *
 * Nothing here is written into the bundle: the name and the logo come from the profile the app
 * fetched for this host, which is what makes one deployment serve any salon (TDD-SA FR-SA-001).
 * While that is in flight the shape is held with a skeleton rather than a platform name, because
 * showing "Simpro" to a Naturals customer, even for a moment, is showing them the wrong business.
 *
 * Inside the booking flow the navigation steps aside for a way out and the salon's number: a
 * customer halfway through a booking needs to finish it or ask for help, not browse.
 */
export function SalonHeader() {
  const { pathname } = useLocation();
  return pathname.startsWith("/book") ? <FlowHeader /> : <SiteHeader />;
}

/**
 * At rest on a phone: the name with the account beside it, the switcher underneath. Scrolled past
 * that, the name folds into the logo and the switcher moves up into one floating pill (design note
 * salon-booking-design-2.md S1); a spacer holds the header's place so the page does not jump. The
 * pill keeps to the screen's width: the theme toggle stays behind and Sign in becomes an icon.
 *
 * From a tablet up it is one sticky row of frosted cream, which gains a hairline once the page moves
 * under it (design note salon-booking-organic-wellness.md section 6 item 1). The salon's name is set in
 * the display face beside its logo, or the board's swaying sage leaf when it has none. On a desktop the
 * places are text links centred in the row, with Book appointment as the filled accent pill on the
 * right. Beside the account sits the light and dark toggle, on every width.
 */
function SiteHeader() {
  const { data: salon, isPending } = useQuery(salonProfileQuery());
  const wide = useMediaQuery("(min-width: 768px)");
  const folded = useScrolledPast(PHONE_REST_HEIGHT) && !wide;
  const scrolled = useScrolledPast(8);

  return (
    <>
      {folded ? <div aria-hidden="true" style={{ height: PHONE_REST_HEIGHT }} /> : null}
      <header
        className={cn(
          "z-30 w-full transition-[background-color,box-shadow,backdrop-filter] duration-300 md:sticky md:top-0 md:bg-[color-mix(in_oklch,var(--t-base)_88%,transparent)] md:backdrop-blur-xl",
          scrolled && !folded && "md:shadow-[inset_0_-1px_0_var(--t-edge-subtle)]",
          folded &&
            "animate-drop-in fixed inset-x-0 top-0 px-3 pt-[max(0.75rem,env(safe-area-inset-top))]",
        )}
      >
        <div
          className={cn(
            "mx-auto w-full max-w-[77.5rem]",
            folded ? "p-0" : "px-4 pt-3.5 pb-2 md:px-8 md:py-3 lg:px-10 lg:py-4",
          )}
        >
          <div
            className={cn(
              "flex items-center",
              folded
                ? "shadow-bar gap-2 rounded-full bg-[color-mix(in_oklch,var(--t-base)_88%,transparent)] p-1.5 pl-2 backdrop-blur-xl"
                : "flex-wrap gap-x-2.5 gap-y-3 md:flex-nowrap md:gap-4",
            )}
          >
            <Link
              to="/"
              viewTransition
              // Named while the profile loads, because a skeleton gives a screen reader nothing to read.
              aria-label={isPending ? "Home" : undefined}
              className={cn(
                "flex min-h-11 min-w-0 items-center gap-3 rounded-2xl",
                folded ? "flex-none" : "flex-1 md:flex-none lg:w-80 lg:flex-initial",
                focusRing,
              )}
            >
              {isPending || !salon ? (
                <>
                  <Skeleton className="size-12 rounded-full md:size-14" />
                  <Skeleton className="h-4 w-36" />
                </>
              ) : (
                <>
                  <SalonMark
                    name={salon.displayName}
                    logoUrl={salon.logoUrl}
                    size={folded ? "sm" : "lg"}
                  />
                  <span
                    className={cn(
                      "headline line-clamp-2 text-[22px] leading-none text-heading lg:text-[28px]",
                      folded && "sr-only",
                    )}
                  >
                    {salon.displayName}
                  </span>
                </>
              )}
            </Link>

            <Switcher
              compact={folded}
              className={cn(
                folded ? "min-w-0 flex-1" : "order-3 w-full md:order-none md:mx-auto md:w-auto",
              )}
            />

            <div className="flex shrink-0 items-center justify-end gap-2 lg:w-80">
              <Link
                to="/book"
                viewTransition
                className={cn(pill.base, pill.accent, "hidden h-12 px-6 text-[15px] lg:inline-flex", focusRing)}
              >
                Book appointment
              </Link>
              {/* A 360px phone cannot fit the toggle in the folded pill without pushing the account off it; it stays in the header at rest. */}
              {folded ? null : <ThemeToggle />}
              <AccountButton compact={folded} />
            </div>
          </div>
        </div>
      </header>
    </>
  );
}

/** In the booking flow: the salon's number for help, and the way out. Back lives in the action bar. */
function FlowHeader() {
  const { data: salon } = useQuery(salonProfileQuery());
  const phone = salon?.branch?.phone;

  return (
    <header className="z-30 w-full">
      <div className="mx-auto w-full max-w-[77.5rem] px-4 pt-3 pb-1 md:px-8 lg:px-10 lg:pt-4">
        <div className="flex items-center justify-between gap-3 lg:border-b lg:border-edge-subtle lg:pb-4">
          {salon ? (
            <Link
              to="/"
              className={cn("hidden h-10 items-center gap-2.5 rounded-full lg:flex", focusRing)}
            >
              <SalonMark name={salon.displayName} logoUrl={salon.logoUrl} />
              <span className="headline text-[19px] text-heading">{salon.displayName}</span>
            </Link>
          ) : null}
          <div id={BOOKING_PROGRESS_SLOT} className="hidden lg:mx-auto lg:block" />
          <div className="flex items-center gap-2.5">
            {/* On a tablet the salon's mark sits beside the number (follow-up F1); a desktop names it on the left. */}
            {salon ? (
              <Link to="/" aria-label={salon.displayName} className={cn("hidden rounded-full md:flex lg:hidden", focusRing)}>
                <SalonMark name={salon.displayName} logoUrl={salon.logoUrl} />
              </Link>
            ) : null}
            {phone ? (
              <a
                href={`tel:${phone}`}
                className={cn(
                  "flex flex-col rounded-2xl text-xs leading-snug text-muted lg:flex-row lg:items-baseline lg:gap-1.5 lg:text-sm",
                  focusRing,
                )}
              >
                Need a hand?
                <span className="text-sm font-bold text-heading">{formatPhone(phone)}</span>
              </a>
            ) : null}
          </div>
          <Link to="/" viewTransition className={cn(pill.base, pill.outline, pill.sm, focusRing)}>
            Exit
            <X className="size-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </header>
  );
}
